/* eslint-disable react/prop-types */
import React from 'react';
import { Box, MenuItem, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { StyledMenu } from './menu.styles';
import { menuPropTypes, menuDefaultProps } from './menu.propTypes';
import { useAuthContext } from './../../../context/AuthContext';
import commonUtil from '../../../utils/commonUtil';

/**
 * Renders a category header with an optional icon.
 * @param {Object} props
 * @param {React.ElementType} props.icon - The icon component to display.
 * @param {string} props.title - The title of the category.
 * @param {Boolean} props.isShowHeader - Display sub-category header if this prop value is true.
 */
const CategoryHeader = ({ icon: IconComponent, title, isShowHeader, isNested = false }) => {
  return isShowHeader ?
    (<Box sx={{ display: 'flex', alignItems: 'center', mb: isNested ? 0.5 : 1 }}>
      {IconComponent && <IconComponent />}
      <Typography
        sx={{
          pl: IconComponent ? 2 : 0,
          whiteSpace: {
            xs: 'normal',
            sm: 'normal',
            md: 'nowrap',
            lg: 'nowrap',
            xl: 'nowrap',
          },
          fontSize: '0.768em',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}

        fontWeight={700}
        variant="h6"
        noWrap
        fontFamily='heading.fontFamily.primary'
      >
        {title}
      </Typography>
    </Box>) : null
};

/**
 * Handle click on menu item.
 * @param {string} link - The link to navigate to.
 * @param {Function} onClose - Function to call when the menu is closed.
 * @param {Object} actions - Frontity actions for routing.
 */
const handleMenuItemClick = (link, onClose) => {
  onClose();
  commonUtil.handleRedirection(link)
};

/**
 * Renders a list of menu items.
 * @param {Array} items - The items to render in the menu.
 * @returns {React.ReactNode} The rendered menu items.
 */
const renderMenuItems = (items, onClose, actions, hasIcon) =>
  items.map((item, index) => (
    <React.Fragment key={index}>
      {item.children ? (
        <Box
          sx={{
            // ml: { xs: 5, sm: 5, md: 5.5 },
            // mt: { sm: 0, md: item.pushDown ? item.pushDown : 0 },
            mb: { xs: '0.5em', sm: '0.5em', md: '0.536em' }
          }}
          className='menu-item'
        >
          {item.children}
        </Box>
      ) : (
        <MenuItem
          disableGutters={true}
          // sx={{ padding: 0, ml: hasIcon ? 5.5 : 0, pl: hasIcon ? 0 : 0, mt: { sm: 0, md: item.pushDown ? item.pushDown : 0 } }}
          sx={{
            ml: hasIcon ? 5 : 0,
            padding: 0,
            // Apply transparent background on hover
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
          onClick={() => handleMenuItemClick(item.link, onClose, actions)}
          className='menu-item'
        >
          {item?.icon && <Box sx={{ pr: 1 }}>{item.icon()}</Box>}
          <Typography
            fontWeight={item.fontWeight ? item.fontWeight : 'normal'}
            fontFamily={'body.fontFamily.secondary'}
            sx={{
              mb: '0.536em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'break-spaces',
              fontSize: '0.542em',
              fontWeight: item?.fontWeight ? item?.fontWeight + " !important" : 'normal'
            }}
          >
            {item.label}
          </Typography>
        </MenuItem>
      )}
    </React.Fragment>
  ));

/**
 * Renders a list of subcategories.
 * @param {Array} subcategories - The subcategories to render.
 * @returns {React.ReactNode} The rendered subcategories.
 */
const renderSubcategories = (subcategories, onClose, actions) =>
  <Box sx={{
    '& > *:not(:last-child)': {
      marginBottom: '1.57em',  // This will apply marginBottom to all children except the last one
    },
  }}>
    {
      subcategories.map((subcategory, subIndex, onClose, actions) => (
        <Box
          key={subIndex}
          sx={{
            mt: { sm: 0, md: subcategory.pushDown ? subcategory.pushDown : 0 },
          }}
          className='nested-sub-menu'
        >
          <CategoryHeader icon={subcategory.icon} title={subcategory.title} isShowHeader={subcategory?.isShowHeader} isNested={true} />
          {renderMenuItems(subcategory.items, onClose, actions, subcategory.icon !== null ? true : false)}
        </Box>
      ))
    }
  </Box>


/**
 * Menu component to display a list of categorized menu items.
 * @param {Object} props
 * @param {HTMLElement} props.anchorEl - The element to anchor the menu to.
 * @param {boolean} props.open - Controls whether the menu is open or closed.
 * @param {Function} props.onClose - Function to call when the menu is closed.
 * @param {Array} props.menuItems - Array of menu categories, each containing title, icon, items, and optional subcategories.
 * @param {number} [props.columns=3] - Number of columns for the grid layout in the menu.
 * @param {string} [props.backgroundColor='#EBEEF2'] - Background color of the menu.
 * @param {string} [props.width] - Width of the menu.
 * @returns {React.ReactNode} The rendered menu component.
 */
export default function Menu({
  anchorEl,
  open,
  onClose,
  menuItems,
  columns,
  backgroundColor,
  // eslint-disable-next-line no-empty-function
  actions = { router: { set: () => { } } },
  onMouseOut,
  onMouseOver,
  className,
  sx,
}) {

  const theme = useTheme();

  const widthMegaMenu = (name) => {
    if (name === "Insurance") {
      return '700px';
    }
    else if (name === "Claims") {
      return '600px';
    }
    else if (name === "Customer Care") {
      return '500px';
    }
    else if (name === "Resources") {
      return '1000px';
    } else {
      return `${columns * 20}%`
    }
  }

  return (
    <StyledMenu
      id="basic-menu"
      theme={theme}
      backgroundcolor={backgroundColor}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={sx}
      maxWidth={widthMegaMenu(className)}
      minWidth={widthMegaMenu(className)}
      MenuListProps={{
        onMouseLeave: onClose
      }}
    >
      <Grid
        container
        columns={12}
      // onMouseOver={onMouseOver}
      // onMouseOut={onMouseOut}
      >

        {menuItems.map((category, index) => (
          <Grid
            key={index}
            xs={12}
            sm={6}
            md={12 / columns}
            sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', padding: '0', }}

          >
            <Box
              sx={{
                padding: '0',
              }}
            >
              {category?.addSubMenuHeader && category?.title?.length > 0 && (
                category?.isShowHeader
                  ? <CategoryHeader icon={category?.icon} title={category.title} isShowHeader={category?.isShowHeader} />
                  : <Box sx={{ height: 'calc(1.4063rem * 1.6)' }}></Box>
                // : <Box gap={1} sx={{height:'calc(1.4063rem * 1.6)',marginBottom:1}}></Box>
              )}
              {category.items &&
                renderMenuItems(category.items, onClose, actions, category?.icon !== null)}
              {category.subcategories &&
                renderSubcategories(category.subcategories, onClose, actions)}
            </Box>
          </Grid>
        ))}
      </Grid>
    </StyledMenu>
  );
}

Menu.propTypes = menuPropTypes;
Menu.defaultProps = menuDefaultProps;
